import {useCallback, type RefCallback} from 'react'

const COMPACTION_BUFFER_PX = 8

function pixelValue(value: string): number {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function outerInlineSize(element: HTMLElement): number {
  const style = getComputedStyle(element)
  return element.getBoundingClientRect().width + pixelValue(style.marginInlineStart) + pixelValue(style.marginInlineEnd)
}

function isFlowChild(child: Element): child is HTMLElement {
  if (!(child instanceof HTMLElement)) return false

  const style = getComputedStyle(child)
  return style.display !== 'none' && style.position !== 'absolute' && style.position !== 'fixed'
}

function flexChildrenInlineSize(element: HTMLElement): number {
  const children = Array.from(element.children).filter(isFlowChild)
  if (children.length === 0) return 0

  const gap = pixelValue(getComputedStyle(element).columnGap)
  return children.reduce((width, child) => width + outerInlineSize(child), 0) + gap * (children.length - 1)
}

function updateCompactState(pickers: HTMLDivElement): void {
  const leading = pickers.parentElement
  const toolbar = leading?.parentElement
  const actions = leading?.nextElementSibling
  if (!(leading instanceof HTMLElement) || !(toolbar instanceof HTMLElement) || !(actions instanceof HTMLElement)) {
    pickers.removeAttribute('data-responsive-compact')
    return
  }

  // Measure the controls expanded, then restore the final state synchronously so
  // the browser never paints the wrapped intermediate layout.
  pickers.removeAttribute('data-responsive-compact')

  const toolbarStyle = getComputedStyle(toolbar)
  const availableWidth =
    toolbar.clientWidth - pixelValue(toolbarStyle.paddingInlineStart) - pixelValue(toolbarStyle.paddingInlineEnd)
  const requiredWidth = flexChildrenInlineSize(leading) + outerInlineSize(actions) + pixelValue(toolbarStyle.columnGap)

  // Compact just before the exact fit boundary to avoid fractional wrapping during resize.
  pickers.toggleAttribute('data-responsive-compact', requiredWidth + COMPACTION_BUFFER_PX > availableWidth)
}

function observeResponsivePickerGroup(node: HTMLDivElement): () => void {
  let active = true
  let updateQueued = false
  let updateTimer: number | undefined
  const resizeTargets = [node.parentElement?.parentElement, node.parentElement?.nextElementSibling, node].filter(
    (target): target is Element => target instanceof Element,
  )
  const observedSizes = new WeakMap<Element, {width: number; height: number}>()
  const update = () => updateCompactState(node)
  const observeResizeTargets = () => {
    for (const target of resizeTargets) {
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop -- These reads are batched without interleaved layout writes.
      const {width, height} = target.getBoundingClientRect()
      observedSizes.set(target, {width, height})
      resizeObserver.observe(target)
    }
  }
  const scheduleUpdate = () => {
    if (updateQueued) return

    updateQueued = true
    updateTimer = window.setTimeout(() => {
      updateTimer = undefined
      updateQueued = false
      if (!active || !node.isConnected) return

      // Run outside the ResizeObserver delivery cycle so expanding the controls
      // for measurement cannot trigger an undelivered-notification loop.
      resizeObserver.disconnect()
      update()
      observeResizeTargets()
    })
  }

  update()

  const resizeObserver = new ResizeObserver(entries => {
    const sizeChanged = entries.some(({target}) => {
      const previousSize = observedSizes.get(target)
      const {width, height} = target.getBoundingClientRect()
      return !previousSize || previousSize.width !== width || previousSize.height !== height
    })
    if (sizeChanged) {
      resizeObserver.disconnect()
      scheduleUpdate()
    }
  })
  const leading = node.parentElement
  observeResizeTargets()

  const mutationObserver = new MutationObserver(scheduleUpdate)
  if (leading) {
    mutationObserver.observe(leading, {
      childList: true,
      characterData: true,
      subtree: true,
    })
  }

  return () => {
    active = false
    if (updateTimer !== undefined) window.clearTimeout(updateTimer)
    resizeObserver.disconnect()
    mutationObserver.disconnect()
  }
}

export function useResponsivePickerGroup(enabled: boolean): RefCallback<HTMLDivElement> {
  return useCallback(
    node => {
      if (!node || !enabled) return
      return observeResponsivePickerGroup(node)
    },
    [enabled],
  )
}
